import React, { useEffect, useState } from "react";
import {
  Box,
  Button,
  CircularProgress,
  IconButton,
  Snackbar,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  Paper,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Typography,
} from "@mui/material";
import MuiAlert from "@mui/material/Alert";
import AddIcon from "@mui/icons-material/Add";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import { useNavigate } from "react-router-dom";
import { useApi } from "../api/useApi";

const AdminBuyerPage = () => {
  const navigate = useNavigate();
  const { authFetch } = useApi();

  const [buyers, setBuyers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: "", severity: "success" });
  const [dialogOpen, setDialogOpen] = useState(false);
  const [buyerToDelete, setBuyerToDelete] = useState(null);
  const [filter, setFilter] = useState("");
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(50);

  const showSnackbar = (message, severity = "success") => {
    setSnackbar({ open: true, message, severity });
  };

  const handleSnackbarClose = () => {
    setSnackbar((prev) => ({ ...prev, open: false }));
  };

  const openDeleteDialog = (buyer) => {
    setBuyerToDelete(buyer);
    setDialogOpen(true);
  };

  const closeDeleteDialog = () => {
    setDialogOpen(false);
    setBuyerToDelete(null);
  };

  const handleDelete = async () => {
    if (!buyerToDelete) return;
    try {
      const response = await authFetch(`/admin/buyer/${buyerToDelete.id}`, {
        method: "DELETE",
      });

      if (!response.ok) throw new Error("Failed to delete buyer");

      setBuyers((prev) => prev.filter((b) => b.id !== buyerToDelete.id));
      showSnackbar("Buyer deleted successfully", "success");
    } catch (error) {
      console.error("Error deleting buyer:", error);
      showSnackbar("Failed to delete buyer.", "error");
    } finally {
      closeDeleteDialog();
    }
  };

  useEffect(() => {
    const fetchBuyers = async () => {
      setLoading(true);
      try {
        const response = await authFetch(`/admin/buyer`);

        if (!response.ok) throw new Error("Failed to fetch buyers");

        const data = await response.json();
        setBuyers(data);
      } catch (error) {
        showSnackbar("Failed to fetch buyers.", "error");
      } finally {
        setLoading(false);
      }
    };

    fetchBuyers();
  }, [authFetch]);

  const filteredBuyers = buyers.filter((u) =>
    [u.firstName, u.lastName, u.email].join(" ").toLowerCase().includes(filter.toLowerCase())
  );

  const paginatedBuyers = filteredBuyers.slice(
    page * rowsPerPage,
    page * rowsPerPage + rowsPerPage
  );

  const handleChangePage = (_, newPage) => setPage(newPage);
  const handleChangeRowsPerPage = (e) => {
    setRowsPerPage(parseInt(e.target.value, 10));
    setPage(0);
  };

  return (
    <Box>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
        <TextField
          label="Search buyers"
          variant="outlined"
          size="small"
          value={filter}
          onChange={(e) => setFilter(e.target.value)}
        />
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => navigate("/admin/buyers/new")}
        >
          Create Buyer
        </Button>
      </Box>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={handleSnackbarClose}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
      >
        <MuiAlert
          elevation={6}
          variant="filled"
          onClose={handleSnackbarClose}
          severity={snackbar.severity}
        >
          {snackbar.message}
        </MuiAlert>
      </Snackbar>

      <Dialog open={dialogOpen} onClose={closeDeleteDialog}>
        <DialogTitle>Confirm Deletion</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete buyer {buyerToDelete?.firstName} {buyerToDelete?.lastName}?
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={closeDeleteDialog}>Cancel</Button>
          <Button onClick={handleDelete} color="error">Delete</Button>
        </DialogActions>
      </Dialog>

      {loading ? (
        <Box display="flex" justifyContent="center" alignItems="center" minHeight={200}>
          <CircularProgress />
        </Box>
      ) : (
        <Paper>
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>First Name</TableCell>
                  <TableCell>Last Name</TableCell>
                  <TableCell>Email</TableCell>
                  <TableCell>Phone</TableCell>
                  <TableCell>WhatsApp</TableCell>
                  <TableCell align="right">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {paginatedBuyers.map((buyer) => (
                  <TableRow key={buyer.id}>
                    <TableCell>{buyer.firstName}</TableCell>
                    <TableCell>{buyer.lastName}</TableCell>
                    <TableCell>{buyer.email}</TableCell>
                    <TableCell>{buyer.phone}</TableCell>
                    <TableCell>{buyer.whatsappPhone}</TableCell>
                    <TableCell align="right">
                      <IconButton onClick={() => navigate(`/admin/buyers/edit/${buyer.id}`)}>
                        <EditIcon color="primary" />
                      </IconButton>
                      <IconButton onClick={() => openDeleteDialog(buyer)}>
                        <DeleteIcon color="error" />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))}
                {paginatedBuyers.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={5} align="center">
                      No buyers found.
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
          <TablePagination
            component="div"
            count={filteredBuyers.length}
            page={page}
            onPageChange={handleChangePage}
            rowsPerPage={rowsPerPage}
            onRowsPerPageChange={handleChangeRowsPerPage}
            rowsPerPageOptions={[5, 10, 25, 50, 100]}
          />
        </Paper>
      )}
    </Box>
  );
};

export default AdminBuyerPage;